import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Phone, Video, Monitor, Radio } from 'lucide-react';

interface TeamChatMeetingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  peerName?: string;
  initialTab?: 'audio' | 'video' | 'screen';
}

/**
 * Future-ready collaboration shell — wire to WebRTC / SFU when infra is available.
 * See `future/webrtcPlaceholder.ts` for adapter contract.
 */
export function TeamChatMeetingModal({ open, onOpenChange, peerName, initialTab = 'audio' }: TeamChatMeetingModalProps) {
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border border-card-border/50 bg-card-bg/95 text-foreground backdrop-blur-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="h-5 w-5 animate-pulse text-cyber-cyan" />
            Huddle & calls
          </DialogTitle>
          <DialogDescription>
            {peerName ? `With ${peerName} — ` : null}
            Signaling and TURN are not connected in this build. UI is ready for WebRTC, Google Meet–style rooms, or your SFU.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-1.5 rounded-xl border border-border/50 bg-muted/15 p-1">
          {(
            [
              { id: 'audio' as const, label: 'Audio', icon: Phone },
              { id: 'video' as const, label: 'Video', icon: Video },
              { id: 'screen' as const, label: 'Screen', icon: Monitor },
            ] as const
          ).map((x) => (
            <Button
              key={x.id}
              type="button"
              variant="ghost"
              size="sm"
              className={`flex-1 rounded-lg text-xs transition-all duration-200 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] ${
                tab === x.id
                  ? 'bg-cyber-cyan text-white shadow-md shadow-cyber-cyan/25 ring-1 ring-cyber-cyan/35 hover:bg-cyber-cyan/90'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              }`}
              onClick={() => setTab(x.id)}
            >
              <x.icon className="h-4 w-4 mr-1.5 shrink-0" />
              {x.label}
            </Button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="rounded-xl border border-border/50 bg-muted/20 p-4 text-sm text-muted-foreground space-y-2"
          >
            <p>
              <span className="text-foreground font-medium">Next step:</span> mount your RTC adapter (getUserMedia, RTCPeerConnection,
              optional WHIP/WHEP) and route meeting IDs through your backend.
            </p>
            <p className="text-xs">
              Incident mode: associate room with alert ID so logs, metrics, and dashboards open beside this modal.
            </p>
          </motion.div>
        </AnimatePresence>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" className="rounded-xl transition-colors duration-200" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button type="button" className="rounded-xl shadow-md shadow-cyber-cyan/15 transition-shadow duration-200 hover:shadow-cyber-cyan/25" disabled>
            Start when connected
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
